import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { usePageTitle } from '../hooks/usePageTitle';
import { formatDate, getUpcomingScheduledSessions } from '../utils/dateUtils';
import { Subscription, Schedule, Course } from '../types';

const SessionPicker: React.FC = () => {
  usePageTitle('Choose Your Session');
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { user } = useAuth();
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState<string | null>(null);
  const [enrollSuccess, setEnrollSuccess] = useState<string | null>(null);
  const [enrollError, setEnrollError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subRes, courseRes, scheduleRes] = await Promise.all([
          authFetch(`/api/subscriptions/${subscriptionId}`),
          fetch('/api/courses'),
          fetch('/api/schedules'),
        ]);

        if (!subRes.ok) {
          setError('Subscription not found or you do not have access.');
          return;
        }
        const sub: Subscription = await subRes.json();
        setSubscription(sub);

        if (courseRes.ok) {
          const courseList: Course[] = await courseRes.json();
          // For Starter tier, show only the selected courses
          if (sub.tier === 'Starter' && sub.selectedCourses && sub.selectedCourses.length > 0) {
            setCourses(courseList.filter((c) => sub.selectedCourses!.includes(c.id)));
          } else {
            // Pro/Team: show all active courses
            setCourses(courseList.filter((c) => c.active));
          }
        }

        if (scheduleRes.ok) {
          const scheduleList: Schedule[] = await scheduleRes.json();
          setSchedules(getUpcomingScheduledSessions(scheduleList));
        }
      } catch (err) {
        console.error('Error fetching session picker data:', err);
        setError('Failed to load session data.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.email && subscriptionId) {
      fetchData();
    }
  }, [user, subscriptionId, authFetch]);

  const handleEnroll = async (scheduleId: string, courseName: string) => {
    if (!user || !subscription) return;

    setEnrolling(scheduleId);
    setEnrollError(null);
    setEnrollSuccess(null);

    try {
      const res = await authFetch('/api/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheduleId,
          studentName: user.name,
          studentEmail: user.email,
          subscriptionId: subscription.id,
        }),
      });

      if (res.ok) {
        setEnrollSuccess(
          `You're enrolled in ${courseName}! A confirmation email has been sent to ${user.email}.`
        );
        // Update seat count locally
        setSchedules((prev) =>
          prev.map((s) =>
            s.id === scheduleId
              ? { ...s, currentEnrollment: s.currentEnrollment + 1 }
              : s
          )
        );
        // For Starter tier (1 course), navigate to dashboard after brief delay
        if (subscription.tier === 'Starter') {
          setTimeout(() => navigate('/dashboard'), 2000);
        }
      } else {
        const data = await res.json();
        setEnrollError(data.message || 'Failed to enroll. Please try again.');
      }
    } catch {
      setEnrollError('An error occurred. Please try again.');
    } finally {
      setEnrolling(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading available sessions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <Link to="/dashboard" className="btn btn-primary">Go to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="session-picker-page">
      <section className="dashboard-header">
        <div className="section-container">
          <h1 className="page-title">Choose Your Session</h1>
          <p className="page-subtitle">
            Select a session below to complete your enrollment.
            {subscription?.tier === 'Starter' && ' (1 course included with your Starter plan)'}
            {subscription?.tier === 'Pro' && ' (Unlimited courses with your Pro plan)'}
          </p>
        </div>
      </section>

      {enrollSuccess && (
        <section className="section">
          <div className="section-container">
            <div className="alert alert-success" style={{ padding: '1rem', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', color: '#155724' }}>
              {enrollSuccess}
            </div>
          </div>
        </section>
      )}
      {enrollError && (
        <section className="section">
          <div className="section-container">
            <div className="alert alert-error" style={{ padding: '1rem', background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '6px', color: '#721c24' }}>
              {enrollError}
            </div>
          </div>
        </section>
      )}

      {courses.map((course) => {
        const courseSchedules = schedules.filter((s) => s.courseId === course.id);
        if (courseSchedules.length === 0) return null;

        return (
          <section key={course.id} className="section">
            <div className="section-container">
              <h2 className="section-title">{course.title}</h2>
              <p style={{ marginBottom: '1rem', color: '#6b7280' }}>{course.shortDescription}</p>
              <div className="sessions-table-wrapper">
                <table className="sessions-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Seats Available</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseSchedules.map((schedule) => {
                      const seatsLeft = schedule.maxSeats - schedule.currentEnrollment;
                      return (
                        <tr key={schedule.id}>
                          <td>
                            {formatDate(schedule.sessionDate, {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </td>
                          <td>{schedule.startTime} - {schedule.endTime}</td>
                          <td>
                            <span className={`seats-indicator ${seatsLeft <= 3 ? 'seats-low' : ''}`}>
                              {seatsLeft} of {schedule.maxSeats}
                            </span>
                          </td>
                          <td>
                            <span className="status-badge status-scheduled">
                              {seatsLeft > 0 ? 'Open' : 'Sold Out'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleEnroll(schedule.id, course.title)}
                              disabled={seatsLeft <= 0 || enrolling === schedule.id}
                            >
                              {seatsLeft <= 0
                                ? 'Sold Out'
                                : enrolling === schedule.id
                                ? 'Enrolling...'
                                : 'Select'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        );
      })}

      {courses.length === 0 && (
        <section className="section">
          <div className="section-container">
            <div className="empty-state">
              <p>No courses with available sessions at this time. Check back soon.</p>
              <Link to="/courses" className="btn btn-primary">Browse Courses</Link>
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="section-container" style={{ textAlign: 'center' }}>
          <Link to="/dashboard" className="btn btn-outline">
            Go to Dashboard
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SessionPicker;
